export type EngineKey =
  "quiddity" | "ject" | "histor" | "echo" | "aegis" | "sceop";

export type GlyphKey = Exclude<EngineKey, "quiddity">;
export type ContainerMode =
  "circle" | "triangle" | "quad" | "strange" | "bubbling";

export interface DotConfig {
  readonly radius: number;
  readonly angle: number;
  readonly size: number;
  readonly speed: number;
  readonly direction: 1 | -1;
  readonly front: boolean;
  readonly color: string;
}

export interface EngineMarkConfig {
  readonly label: string;
  readonly subtitle: string;
  readonly force: string;
  readonly glyph: GlyphKey | null;
  readonly dots: readonly DotConfig[];
  readonly container: {
    readonly modes: readonly ContainerMode[];
    readonly morph: number;
    readonly wobble: number;
    readonly rotation: number;
    readonly changeEvery: number;
  };
}

/**
 * Derived from the v1.0 source geometry and v1.0.1 identity registry.
 * Glyph geometry is defined separately in glyphs.tsx and is unchanged.
 */
export const ENGINE_MARKS: Readonly<Record<EngineKey, EngineMarkConfig>> = {
  quiddity: {
    label: "Quiddity",
    subtitle: "Living Foundation",
    force: "Harmony",
    glyph: null,
    dots: [
      {
        radius: 74,
        angle: 28,
        size: 5,
        speed: 0.2,
        direction: 1,
        front: true,
        color: "#8FD7FF",
      },
      {
        radius: 95,
        angle: 155,
        size: 6,
        speed: 0.14,
        direction: -1,
        front: false,
        color: "#C4A6FF",
      },
      {
        radius: 113,
        angle: 278,
        size: 4,
        speed: 0.16,
        direction: 1,
        front: true,
        color: "#8EF0C9",
      },
    ],
    container: {
      modes: ["circle", "triangle", "quad", "strange", "bubbling"],
      morph: 0.22,
      wobble: 0.12,
      rotation: 8,
      changeEvery: 2.8,
    },
  },
  ject: {
    label: "Ject",
    subtitle: "Deliberation & Shared Meaning Engine",
    force: "Convergence",
    glyph: "ject",
    dots: [
      {
        radius: 78,
        angle: 35,
        size: 5,
        speed: 0.13,
        direction: 1,
        front: true,
        color: "#9FB7FF",
      },
      {
        radius: 96,
        angle: 190,
        size: 5,
        speed: 0.1,
        direction: -1,
        front: false,
        color: "#6EE6D8",
      },
      {
        radius: 112,
        angle: 305,
        size: 4,
        speed: 0.08,
        direction: 1,
        front: true,
        color: "#FFD37A",
      },
    ],
    container: {
      modes: ["quad", "strange", "quad"],
      morph: 0.07,
      wobble: 0.04,
      rotation: 3,
      changeEvery: 5.5,
    },
  },
  histor: {
    label: "Histor",
    subtitle: "Provenance & Trust Ledger Engine",
    force: "Memory",
    glyph: "histor",
    dots: [
      {
        radius: 75,
        angle: 26,
        size: 5,
        speed: 0.12,
        direction: 1,
        front: false,
        color: "#E7B86A",
      },
      {
        radius: 93,
        angle: 165,
        size: 6,
        speed: 0.1,
        direction: -1,
        front: true,
        color: "#BDA1FF",
      },
      {
        radius: 108,
        angle: 286,
        size: 4,
        speed: 0.08,
        direction: 1,
        front: true,
        color: "#8DD8FF",
      },
    ],
    container: {
      modes: ["quad", "strange", "quad"],
      morph: 0.065,
      wobble: 0.035,
      rotation: 2.5,
      changeEvery: 5.5,
    },
  },
  echo: {
    label: "Echo",
    subtitle: "Continuity & Restoration Engine",
    force: "Continuity",
    glyph: "echo",
    dots: [
      {
        radius: 76,
        angle: 60,
        size: 5,
        speed: 0.14,
        direction: -1,
        front: true,
        color: "#8EF0C9",
      },
      {
        radius: 94,
        angle: 220,
        size: 6,
        speed: 0.09,
        direction: 1,
        front: false,
        color: "#87D7FF",
      },
      {
        radius: 111,
        angle: 330,
        size: 4,
        speed: 0.11,
        direction: -1,
        front: true,
        color: "#E9F3FF",
      },
    ],
    container: {
      modes: ["circle", "strange", "circle"],
      morph: 0.06,
      wobble: 0.035,
      rotation: 2.5,
      changeEvery: 5.5,
    },
  },
  aegis: {
    label: "Aegis",
    subtitle: "Policy & Protected Interface Engine",
    force: "Protection",
    glyph: "aegis",
    dots: [
      {
        radius: 78,
        angle: 12,
        size: 5,
        speed: 0.1,
        direction: 1,
        front: false,
        color: "#7AA9FF",
      },
      {
        radius: 96,
        angle: 140,
        size: 6,
        speed: 0.07,
        direction: -1,
        front: true,
        color: "#F2C66D",
      },
      {
        radius: 114,
        angle: 260,
        size: 4,
        speed: 0.08,
        direction: 1,
        front: true,
        color: "#D9E7FF",
      },
    ],
    container: {
      modes: ["quad", "triangle", "quad"],
      morph: 0.055,
      wobble: 0.025,
      rotation: 1.5,
      changeEvery: 6.5,
    },
  },
  sceop: {
    label: "Sceop",
    subtitle: "Multimedia Generation Orchestration Engine",
    force: "Making",
    glyph: "sceop",
    dots: [
      {
        radius: 76,
        angle: 42,
        size: 5,
        speed: 0.16,
        direction: 1,
        front: true,
        color: "#FF9FD7",
      },
      {
        radius: 97,
        angle: 178,
        size: 6,
        speed: 0.11,
        direction: -1,
        front: false,
        color: "#91B8FF",
      },
      {
        radius: 113,
        angle: 300,
        size: 4,
        speed: 0.13,
        direction: 1,
        front: true,
        color: "#FFD37A",
      },
    ],
    container: {
      modes: ["triangle", "strange", "triangle"],
      morph: 0.075,
      wobble: 0.04,
      rotation: 3.2,
      changeEvery: 5,
    },
  },
};
